import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { genericFormInterface } from 'src/app/util/interfaces/genericForm';
import { GenericFormGenModule } from 'src/app/page_component_item/generic-form-gen/generic-form-gen.module';

/**
 * Componente genérico standalone para campos do tipo json_dynamic.
 *
 * Uso:
 *   <pcp-json-dynamic-field
 *     [field]="cf"
 *     [disabled]="isLocked"
 *     [labels]="labels"
 *     (valueChange)="onCustomfieldChange()">
 *   </pcp-json-dynamic-field>
 */
@Component({
	selector: 'pcp-json-dynamic-field',
	templateUrl: './json-dynamic-field.component.html',
	styleUrls: ['./json-dynamic-field.component.scss'],
	standalone: true,
	imports: [CommonModule, GenericFormGenModule],
})
export class JsonDynamicFieldComponent implements OnInit, OnChanges {
	@Input() field: genericFormInterface;
	@Input() disabled = false;
	@Input() labels: any = {};

	/** Emite quando o valor é alterado (para o pai chamar verifyCustomfieldValues) */
	@Output() valueChange = new EventEmitter<void>();

	/** Objeto JSON do item selecionado — popula os sub-campos */
	selectedData: any = null;

	ngOnInit(): void {
		// Garante que o campo começa como select-iac
		if (!this.disabled) this.field.inputType = 'select-iac';

		// Registra o extraFunction para capturar a seleção do cpi-input-with-autocomplete
		this.field.extraFunction = (value: any) => this.onSelect(value);

		// Restaura valor salvo (rawValues[0] = JSON string)
		this._restoreFromSaved();

		if (this.disabled) this._applyLock();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes.disabled || changes.disabled.firstChange) return;
		if (changes.disabled.currentValue) {
			this._applyLock();
		} else {
			this._applyUnlock();
		}
	}

	onSelect(selectedId: any): void {
		const found = this.field.items?.find((item: any) => String(item.id) === String(selectedId));
		this.selectedData = found ? found.data : null;
		// values[0] = autoId → select-iac exibe label correto
		this.field.values = [selectedId];
		// rawValues[0] = label text → enviado ao banco
		this.field.rawValues = [found ? (found.label ?? found.name ?? '') : ''];
		// rawJsonValue = JSON serializado do objeto selecionado → salvo em value_json no banco
		this.field.rawJsonValue = this.selectedData ? JSON.stringify(this.selectedData) : '';
		this.field.alert = false;
		this.valueChange.emit();
	}

	getSubFieldKeys(): string[] {
		if (!this.selectedData) return [];
		return Object.keys(this.selectedData).filter((k) => k.toLowerCase() !== 'information');
	}

	getInformationText(): string | null {
		if (!this.selectedData) return null;
		const key = Object.keys(this.selectedData).find((k) => k.toLowerCase() === 'information');
		return key ? (this.selectedData[key] ?? null) : null;
	}

	getInformationSegments(): Array<{ type: 'text' | 'link' | 'break'; value: string }> {
		const text = this.getInformationText();
		if (!text) return [];
		const urlRegex = /https?:\/\/\S+/g;
		const result: Array<{ type: 'text' | 'link' | 'break'; value: string }> = [];

		// Divide em parágrafos (duplo newline)
		const paragraphs = text.split(/\n\n/);
		for (const [pIndex, paragraph] of paragraphs.entries()) {
			if (pIndex > 0) result.push({ type: 'break', value: '' });

			// Dentro de cada parágrafo, detecta URLs
			urlRegex.lastIndex = 0;
			let lastIndex = 0;
			let match: RegExpExecArray | null;
			while ((match = urlRegex.exec(paragraph)) !== null) {
				if (match.index > lastIndex) {
					result.push({ type: 'text', value: paragraph.slice(lastIndex, match.index) });
				}
				result.push({ type: 'link', value: match[0] });
				lastIndex = match.index + match[0].length;
			}
			if (lastIndex < paragraph.length) {
				result.push({ type: 'text', value: paragraph.slice(lastIndex) });
			}
		}
		return result;
	}

	// ── privados ─────────────────────────────────────────────────────────────

	private _restoreFromSaved(): void {
		const savedString = this.field.rawValues?.[0];
		if (savedString) {
			// Formato novo: rawValues[0] é o texto do label
			const matchedByLabel = this.field.items?.find((item: any) => (item.label ?? item.name) === savedString);
			if (matchedByLabel) {
				this.selectedData = matchedByLabel.data;
				this.field.values = [matchedByLabel.id];
				this.field.rawJsonValue = matchedByLabel.data ? JSON.stringify(matchedByLabel.data) : '';
				return;
			}
			// Compat retroativa: rawValues[0] pode ser o JSON completo do objeto
			try {
				const saved = JSON.parse(savedString);
				if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
					this.selectedData = saved;
					const matched = this.field.items?.find((item: any) => JSON.stringify(item.data) === JSON.stringify(saved));
					if (matched) {
						this.field.values = [matched.id];
						// Migra para o novo formato
						this.field.rawValues = [matched.label ?? matched.name ?? ''];
					}
					this.field.rawJsonValue = JSON.stringify(saved);
					return;
				}
			} catch {}
		}
		// Legado: values[0] pode ser o autoId numérico
		if (this.field.values?.[0]) {
			const found = this.field.items?.find((item: any) => String(item.id) === String(this.field.values[0]));
			if (found) {
				this.selectedData = found.data;
				this.field.rawValues = [found.label ?? found.name ?? ''];
				this.field.rawJsonValue = found.data ? JSON.stringify(found.data) : '';
			}
		}
	}

	private _buildDisplayLabel(): string {
		if (!this.selectedData || !this.field.displayKeys?.length) return '';
		return this.field.displayKeys
			.map((k) => this.selectedData[k] ?? '')
			.filter(Boolean)
			.join(' - ');
	}

	private _applyLock(): void {
		this.field.inputType = 'string';
		// rawValues[0] já é o label salvo; fallback para computed label
		const label = this.field.rawValues?.[0] || this._buildDisplayLabel();
		if (label) this.field.values = [label];
	}

	private _applyUnlock(): void {
		this.field.inputType = 'select-iac';
		// Garante que extraFunction aponta para onSelect (pode ter sido sobrescrita externamente)
		this.field.extraFunction = (value: any) => this.onSelect(value);
		// Restaura autoId numérico para o select-iac a partir do label salvo
		const labelString = this.field.rawValues?.[0];
		if (labelString) {
			const matched = this.field.items?.find((item: any) => (item.label ?? item.name) === labelString);
			if (matched) {
				this.field.values = [matched.id];
				return;
			}
			// Compat retroativa: pode ser JSON
			try {
				const saved = JSON.parse(labelString);
				const matchedByData = this.field.items?.find((item: any) => JSON.stringify(item.data) === JSON.stringify(saved));
				if (matchedByData) this.field.values = [matchedByData.id];
			} catch {}
		}
	}
}
